import { Location, NavigateFunction } from "react-router";
import { format } from "date-fns";
import DOMPurify from "dompurify";

import User from "./ts/models/User";
import Event from "./ts/models/Event";
import Patient from "./ts/models/Patient";
import Order from "./ts/models/Order";

export const getClasses = (...classes: string[]): string => {
  return classes.join(" ");
};

export const getUTCDateString = (timestamp: number) => {
  const date = new Date(timestamp);
  const year = date.getUTCFullYear();
  let month = (date.getUTCMonth() + 1).toString();
  let day = date.getUTCDate().toString();

  if (month.length < 2) {
    month = "0" + month;
  }

  if (day.length < 2) {
    day = "0" + day;
  }

  return `${year}-${month}-${day}`;
};

export const getStartOfDay = (value: string) => {
  return value + "T00:00:00";
};

export const getEndOfDay = (value: string) => {
  return value + "T23:59:59";
};

export const getStartOfDayTimestamp = (value: string) => {
  return getTimestampFromDateValue(getStartOfDay(value));
};

export const getEndOfDayTimestamp = (value: string) => {
  return getTimestampFromDateValue(getEndOfDay(value));
};

export const getTimestampFromDateValue = (value: string) => {
  // Takes a date or date time value in string format, and creates a date object from it.
  // YYYY-MM-DD
  // YYYY-MM-DDTHH:mm
  return new Date(value).getTime();
};

export const getLocalDateString = (timestamp: number): string => {
  if (isNaN(timestamp)) {
    return "";
  }
  return format(new Date(timestamp), "yyyy-MM-dd");
};

export const getLocalDateTimeString = (timestamp: number) => {
  if (isNaN(timestamp)) {
    return "";
  }
  return format(new Date(timestamp), "yyyy-MM-dd'T'HH:mm");
};

export const getLocalTimeString = (timestamp: number) => {
  if (isNaN(timestamp)) {
    return "";
  }
  return format(new Date(timestamp), "HH:mm");
};

export const getFormattedDate = (timestamp: number) => {
  if (isNaN(timestamp)) {
    return "";
  }
  // Jun 2nd, 2023
  return format(new Date(timestamp), "MMM do, yyyy");
};

export const getFormattedTime = (timestamp: number) => {
  if (isNaN(timestamp)) {
    return "";
  }
  // 10:20 am
  return format(new Date(timestamp), "h:mm aaa");
};

export const getFormattedDateTime = (timestamp: number) => {
  if (isNaN(timestamp)) {
    return "";
  }
  // Jun 2nd, 2023 10:20 am
  return format(new Date(timestamp), "MMM do, yyyy hh:mm aaa");
};

export const validateEmail = (email: string) => {
  let validEmail = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/;
  if (!email.match(validEmail)) {
    return false;
  }
  return true;
};

export const navigationBack = (
  location: Location,
  navigate: NavigateFunction
) => {
  const path = location.pathname;
  const newPath = path.substring(0, path.lastIndexOf("/"));
  navigate(newPath);
};

export const getUserFullName = (user: User) => {
  let fullName = user.firstName;
  if (user.middleName) {
    fullName += " " + user.middleName;
  }
  if (user.lastName) {
    fullName += " " + user.lastName;
  }
  return fullName;
};

export const getPatientFullName = (patient: Patient) => {
  let fullName = patient.firstName;
  if (patient.middleName) {
    fullName += " " + patient.middleName;
  }
  if (patient.lastName) {
    fullName += " " + patient.lastName;
  }
  return fullName;
};

export const getEventFullAddress = (event: Event) => {
  const address = [
    event.address,
    event.ward,
    event.district,
    event.city,
    event.state,
  ];

  let fullAddress = address.filter((element) => Boolean(element)).join(", ");
  if (event.zipCode) {
    fullAddress = `${fullAddress} ${event.zipCode}`;
  }
  if (event.facilityId) {
    fullAddress = `${fullAddress}, Facility ${event.facilityId}`;
  }
  if (event.schoolId) {
    fullAddress = `${fullAddress}, School ${event.schoolId}`;
  }
  return fullAddress;
};

export const getPatientFullAddress = (event: Patient) => {
  const address = [event.address, event.city, event.state, event.country];

  let fullAddress = address.filter((element) => Boolean(element)).join(", ");
  if (event.zipCode) {
    fullAddress = `${fullAddress} ${event.zipCode}`;
  }
  return fullAddress;
};

export const copyTextToClipboard = (textToCopy: string) => {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(textToCopy);
  } else {
    let textArea = document.createElement("textarea");
    textArea.value = textToCopy;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    return new Promise<void>((resolve, reject) => {
      document.execCommand("copy") ? resolve() : reject();
      textArea.remove();
    });
  }
};

export const downloadFile = (response: any, fileName: string) => {
  let a = document.createElement("a");
  let url = window.URL.createObjectURL(response.data);
  a.href = url;
  a.download = fileName;
  a.click();
  window.URL.revokeObjectURL(url);
  return response;
};

export const getOrderPDFFileName = (order: Order) => {
  let fileName = "ReyaOrder";
  if (order.code) {
    fileName += "_" + order.code;
  }
  if (order.appointmentTime) {
    var formattedDate = getLocalDateString(order.appointmentTime);
    formattedDate = formattedDate.replaceAll("-", "_");
    fileName += "_" + formattedDate;
  }
  fileName += ".pdf";

  return fileName;
};

export const addLocalOffsetToTimestamp = (timestamp: number) => {
  const offset = new Date(timestamp).getTimezoneOffset() * 60000;
  return timestamp + offset;
};

export const sanitizedHtml = (dirtyHtml: any) => {
  // Add a hook that runs after attributes have been sanitized
  DOMPurify.addHook("afterSanitizeAttributes", function (node) {
    // If the node is an anchor tag and has the `target` attribute
    if ("target" in node) {
      node.setAttribute("rel", "noopener noreferrer");
    }
  });
  return DOMPurify.sanitize(dirtyHtml, { ADD_ATTR: ["target"] });
};

export const getStates = [
  { stateName: "Alabama", alphaCode: "AL" },
  { stateName: "Alaska", alphaCode: "AK" },
  { stateName: "Arizona", alphaCode: "AZ" },
  { stateName: "Arkansas", alphaCode: "AR" },
  { stateName: "California", alphaCode: "CA" },
  { stateName: "Colorado", alphaCode: "CO" },
  { stateName: "Connecticut", alphaCode: "CT" },
  { stateName: "Delaware", alphaCode: "DE" },
  { stateName: "Florida", alphaCode: "FL" },
  { stateName: "Georgia", alphaCode: "GA" },
  { stateName: "Hawaii", alphaCode: "HI" },
  { stateName: "Idaho", alphaCode: "ID" },
  { stateName: "Illinois", alphaCode: "IL" },
  { stateName: "Indiana", alphaCode: "IN" },
  { stateName: "Iowa", alphaCode: "IA" },
  { stateName: "Kansas", alphaCode: "KS" },
  { stateName: "Kentucky", alphaCode: "KY" },
  { stateName: "Louisiana", alphaCode: "LA" },
  { stateName: "Maine", alphaCode: "ME" },
  { stateName: "Maryland", alphaCode: "MD" },
  { stateName: "Massachusetts", alphaCode: "MA" },
  { stateName: "Michigan", alphaCode: "MI" },
  { stateName: "Minnesota", alphaCode: "MN" },
  { stateName: "Mississippi", alphaCode: "MS" },
  { stateName: "Missouri", alphaCode: "MO" },
  { stateName: "Montana", alphaCode: "MT" },
  { stateName: "Nebraska", alphaCode: "NE" },
  { stateName: "Nevada", alphaCode: "NV" },
  { stateName: "New Hampshire", alphaCode: "NH" },
  { stateName: "New Jersey", alphaCode: "NJ" },
  { stateName: "New Mexico", alphaCode: "NM" },
  { stateName: "New York", alphaCode: "NY" },
  { stateName: "North Carolina", alphaCode: "NC" },
  { stateName: "North Dakota", alphaCode: "ND" },
  { stateName: "Ohio", alphaCode: "OH" },
  { stateName: "Oklahoma", alphaCode: "OK" },
  { stateName: "Oregon", alphaCode: "OR" },
  { stateName: "Pennsylvania", alphaCode: "PA" },
  { stateName: "Rhode Island", alphaCode: "RI" },
  { stateName: "South Carolina", alphaCode: "SC" },
  { stateName: "South Dakota", alphaCode: "SD" },
  { stateName: "Tennessee", alphaCode: "TN" },
  { stateName: "Texas", alphaCode: "TX" },
  { stateName: "Utah", alphaCode: "UT" },
  { stateName: "Vermont", alphaCode: "VT" },
  { stateName: "Virginia", alphaCode: "VA" },
  { stateName: "Washington", alphaCode: "WA" },
  { stateName: "West Virginia", alphaCode: "WV" },
  { stateName: "Wisconsin", alphaCode: "WI" },
  { stateName: "Wyoming", alphaCode: "WY" },
];
